<template>
    <div>
        <!-- Email Import Banner — shown for hotel or flight redemptions -->
        <div
            v-if="showEmailImportBanner && isHotelOrFlight"
            class="flex flex-col sm:flex-row w-full items-start sm:items-end gap-3 sm:gap-8 p-4 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm mb-6 relative bg-white dark:bg-slate-800"
            :class="{ 'border-[#FEAF9F] dark:border-[#7c2d12]': !isSubscribed }"
            :style="bannerStyle"
        >
            <button
                class="absolute top-3 right-3 sm:hidden flex items-center justify-center p-1 rounded text-slate-400 hover:text-slate-600 dark:text-slate-300"
                @click="dismissEmailImportBanner"
            >
                <Icon icon="close" :width="20" :height="20" />
            </button>
            <div class="flex items-start gap-3 flex-1 min-w-0">
                <Icon
                    icon="email-forward"
                    class="shrink-0 text-[#FD421D]"
                    :width="20"
                    :height="20"
                    pathClass="text-[#FD421D]"
                />
                <div class="flex flex-col justify-center items-start gap-1 flex-1">
                    <p class="text-base font-medium leading-6 text-[#1E293B] dark:text-slate-100 pr-6 sm:pr-0">
                        {{
                            isSubscribed
                                ? 'Import redemptions from email'
                                : 'Import redemptions from email with 10xTravel+'
                        }}
                    </p>
                    <p class="text-sm font-normal leading-5 text-[#64748B] dark:text-slate-400">
                        {{
                            isSubscribed
                                ? 'Forward your flight and hotel confirmation emails to redemptions@10xtravel.com to add them to trips automatically.'
                                : 'Forward your flight and hotel confirmation emails to add them to trips automatically.'
                        }}
                    </p>
                </div>
            </div>
            <div class="mt-3 flex justify-end h-full">
                <div class="flex items-center gap-2 shrink-0 w-full sm:w-auto">
                    <template v-if="isSubscribed">
                        <button
                            type="button"
                            class="text-sm text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 px-4 py-2 flex-1 sm:flex-none"
                            @click="dismissEmailImportBanner"
                        >
                            Dismiss
                        </button>
                        <button
                            type="button"
                            @click="showLearnMoreModal = true"
                            class="flex items-center justify-center flex-1 sm:flex-none px-4 py-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm text-sm font-medium text-[#1E293B] dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
                        >
                            Learn more
                        </button>
                    </template>
                    <template v-else>
                        <button
                            type="button"
                            @click="showLearnMoreModal = true"
                            class="flex items-center justify-center flex-1 sm:flex-none px-4 py-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm text-sm font-medium text-[#1E293B] dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
                        >
                            Learn more
                        </button>
                        <button
                            type="button"
                            @click="showUpgradeModal = true"
                            class="flex items-center justify-center flex-1 sm:flex-none px-4 py-2 rounded bg-[#FD421D] shadow-sm text-sm font-medium leading-5 text-white hover:bg-[#e53b19]"
                        >
                            Upgrade
                        </button>
                    </template>
                </div>
                <button
                    class="hidden sm:flex shrink-0 items-center justify-center p-2 rounded text-slate-400 hover:text-slate-600 dark:text-slate-300 absolute right-0 top-0"
                    @click="dismissEmailImportBanner"
                >
                    <Icon icon="close" :width="20" :height="20" />
                </button>
            </div>
        </div>

        <teleport to="body">
            <EmailImportModal
                :show="showLearnMoreModal"
                :isSubscribed="isSubscribed"
                :userEmail="userEmail"
                @close="showLearnMoreModal = false"
            />
            <UpgradeModal
                :show="showUpgradeModal"
                description="This feature is part of the 10xTravel+ plan. Upgrade to unlock it and get full access."
                @close="showUpgradeModal = false"
            />
        </teleport>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import EmailImportModal from '@/components/trips/modals/email-import-modal.vue';
import UpgradeModal from '@/modals/upgrade-modal.vue';
import { useEmailImport } from '@/composables/useEmailImport';
import { useThemeStore } from '@/store/theme';

const themeStore = useThemeStore();

const props = defineProps({
    redemptionType: { type: Object, default: null },
});

const {
    isSubscribed,
    userEmail,
    showLearnMoreModal,
    showUpgradeModal,
    showEmailImportBanner,
    dismissEmailImportBanner,
} = useEmailImport();

const isHotelOrFlight = computed(
    () =>
        props.redemptionType?.slug === 'hotel' ||
        props.redemptionType?.slug === 'flight'
);

const bannerStyle = computed(() => {
    if (isSubscribed.value) {
        return '';
    }
    const baseColor = themeStore.isDark ? '#1e293b' : '#fff';
    return `background: linear-gradient(66deg, rgba(253, 96, 29, 0) 25.49%, rgba(253, 96, 29, 0.12) 77.35%, rgba(253, 96, 29, 0) 112.21%), ${baseColor};`;
});
</script>
